// Module: useVisualOverrides.js
// Purpose: THE one frontend authority for the per-strategy display record (board 50-8 + 50-21,
//          `strategies.visual_overrides`, GET/PUT /strategies/{id}/visual-overrides). Extracted from
//          IndicatorsPanelTab.useVisualOverridesStore (50-6) so the Strategy Builder and Live Analysis
//          mount the SAME state machine. Exports:
//            - loadVisualOverridesRecord / sanitizeVisualRecord  (read + drop non-member keys on load)
//            - useVisualOverridesRecord   (read-only surfaces: ONE GET per mount, zero PUTs)
//            - useVisualOverridesStore    (the autosave writer; one PUT in flight per strategy ID)
//            - buildOverridesSavePayload  (pure full-record save payload builder)
//            - VisualOverridesContext / useNodeVisualOverrides (node cards read + write through the one
//              mounted store; no card calls setNodes for display keys)
//            - VisualOverridesNotice  (the ONE inline notice both surfaces render: too-large + load failed)
//          This is the ONLY module allowed to import `saveVisualOverrides` (ESLint no-restricted-imports).

import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import StrategyService, { saveVisualOverrides } from '../api/services/strategies';
import {
  patchNodeData,
  applyVisualOverrides,
  diffPatch,
  pickVisualMembers,
} from '../pages/Strategies/Modern/StrategyBuilder/utils/chartDataDerivation';

//! AC7.5 — the one inline notice text shown when the server rejects the record as too large (422
//! BODY_TOO_LARGE). Local state is kept and nothing is retried.
export const VISUAL_OVERRIDES_TOO_LARGE_NOTICE = 'Display settings too large to save';
//! A-3 — shown when the record could not be loaded: display writes are disabled (never fall back to
//! node data for a saved strategy, never PUT without an If-Match).
export const VISUAL_OVERRIDES_UNAVAILABLE_NOTICE = 'Display settings unavailable \u2014 reload';
export const VISUAL_OVERRIDES_LOADING_NOTICE = 'Loading display settings\u2026';

//! F-arb-4 — the ONE rule for "why are the display controls locked on a SAVED strategy": the record is
//  still loading (a transient window — nothing typed is dropped silently, the controls are locked and say
//  so), the load failed, or the record is read-only. `null` = writable (or an unsaved strategy, which has
//  its own "Save the strategy first" state in the panel).
export const displayLockHintFor = (strategyId, { displayWritable, isLoaded, loadFailed }) => {
  if (!strategyId || displayWritable) return null;
  if (!isLoaded) return VISUAL_OVERRIDES_LOADING_NOTICE;
  return loadFailed ? VISUAL_OVERRIDES_UNAVAILABLE_NOTICE : 'Display settings are read-only';
};

//--> A-2 — the ONE notice element, rendered by BOTH surfaces (Strategy Builder root, Live Analysis
//    Indicators tab) from the ONE store. No JSX here: this module is a hook module (no .jsx).
const NOTICE_CLASS = 'fixed bottom-4 left-1/2 -translate-x-1/2 z-50 rounded-md border border-[var(--divider)] '
  + 'bg-bg-2 px-3 py-1.5 text-xs text-fg-1 shadow-lg pointer-events-none';
export function VisualOverridesNotice({ store }) {
  if (!store) return null;
  if (store.bodyTooLarge) {
    return createElement('div', { role: 'status', 'data-testid': 'visual-overrides-too-large', className: NOTICE_CLASS },
      VISUAL_OVERRIDES_TOO_LARGE_NOTICE);
  }
  if (store.isLoaded && store.loadFailed) {
    return createElement('div', { role: 'status', 'data-testid': 'visual-overrides-unavailable', className: NOTICE_CLASS },
      VISUAL_OVERRIDES_UNAVAILABLE_NOTICE);
  }
  return null;
}

const isPlainObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

/*>>> LOAD (read path) <<<*/
//! AC7.7 / D23 — a record written before the key allow-list existed may hold non-member keys per
//! node entry (`label`, `params`, ...). Drop them on load, BEFORE any re-send, so autosave never
//! hits 422 INVALID_VISUAL_OVERRIDES. A record holding only member keys is re-sent unchanged.
export function sanitizeVisualRecord(overrides) {
  const out = {};
  for (const [nodeId, entry] of Object.entries(overrides || {})) {
    if (isPlainObj(entry)) out[nodeId] = pickVisualMembers(entry);
  }
  return out;
}

//--> GET + sanitize. `etag` lives in the BODY (D38), never the ETag header. Throws on failure.
export async function loadVisualOverridesRecord(strategyId) {
  const res = await StrategyService.getVisualOverrides(strategyId);
  const body = res?.data?.data || {};
  return {
    overrides: sanitizeVisualRecord(body.overrides),
    etag: body.etag ?? null,
    canEdit: !!body.can_edit,
  };
}

//--> read-only record hook: exactly ONE GET per mount (per strategyId), zero when there is no id
//*   (an unsaved strategy), never a PUT. The writer hook below composes this — one load path.
export function useVisualOverridesRecord(strategyId) {
  const [overridesSaved, setOverridesSaved] = useState({});
  const [currentEtag, setCurrentEtag] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  //! "Latest ref" pattern — async callbacks (debounce, 412 retry, coalesced follow-up) read the
  //! CURRENT value, never one captured stale at schedule time.
  const overridesSavedRef = useRef(overridesSaved);
  const currentEtagRef = useRef(currentEtag);
  overridesSavedRef.current = overridesSaved;
  currentEtagRef.current = currentEtag;
  const loadSeqRef = useRef(0);

  //--> apply a (sanitized) record + etag to state AND refs synchronously. `canEdit` only changes
  //    when the caller supplies it (a PUT response does not carry it).
  const applyRecord = useCallback(({ overrides, etag, canEdit: nextCanEdit }) => {
    const next = overrides || {};
    setOverridesSaved(next);
    overridesSavedRef.current = next;
    setCurrentEtag(etag ?? null);
    currentEtagRef.current = etag ?? null;
    if (nextCanEdit !== undefined) setCanEdit(!!nextCanEdit);
  }, []);

  //--> Load (independent of the version-config fetch, D38) — tab-resume/fresh mount both land
  //    here, treated as a fresh REST load (project_tab_resume_refresh_parked).
  const reload = useCallback(async () => {
    if (!strategyId) return false;
    const seq = ++loadSeqRef.current;
    try {
      const record = await loadVisualOverridesRecord(strategyId);
      if (seq !== loadSeqRef.current) return null; // superseded (strategy changed / unmounted)
      applyRecord(record);
      setLoadFailed(false);
      setIsLoaded(true);
      return true;
    } catch (e) {
      if (seq !== loadSeqRef.current) return null;
      //! D38/V2-N7a: a failed load is read-only — no If-Match-less write is ever possible.
      applyRecord({ overrides: {}, etag: null, canEdit: false });
      setLoadFailed(true);
      setIsLoaded(true);
      return false;
    }
  }, [strategyId, applyRecord]);

  useEffect(() => {
    applyRecord({ overrides: {}, etag: null, canEdit: false });
    setLoadFailed(false);
    setIsLoaded(false);
    if (strategyId) reload();
    return () => { loadSeqRef.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategyId, reload]);

  return useMemo(() => ({
    overridesSaved,
    currentEtag,
    canEdit,
    loadFailed,
    isLoaded,
    reload,
    applyRecord,
    overridesSavedRef,
    currentEtagRef,
  }), [overridesSaved, currentEtag, canEdit, loadFailed, isLoaded, reload, applyRecord]);
}

//* 50-24 — the Fetch keepalive quota (64 KiB); equals the server's body cap (strategies.py).
export const KEEPALIVE_MAX_BODY_BYTES = 65536;

/*>>> PURE SAVE-PAYLOAD BUILDER (02-design.md §C helper 2 / §H Save) <<<*/
//! M1-C1 FIX: iterate the UNION of overridesSaved ∪ sent ids, never "touched ids only" — an
//! untouched saved id must survive every Save unchanged.
//! M2-1 FIX: an id with no matching node in `versionNodes` (saved under a DIFFERENT version than
//! the one currently loaded) is carried VERBATIM — `versionNode.data` is NEVER dereferenced for
//! that id.
export function buildOverridesSavePayload(versionNodes, overridesSaved, sent) {
  const saved = overridesSaved || {};
  const sentMap = sent || {};
  const ids = new Set([...Object.keys(saved), ...Object.keys(sentMap)]);
  const toSave = {};
  for (const id of ids) {
    const versionNode = (versionNodes || []).find((n) => n.id === id);
    //! ROOT-CAUSE FIX (model-indicator colour change deleted label/outputs/...): the candidate
    //  handed to `diffPatch` must be the FULL effective node data (version ⊕ saved ⊕ sent), never
    //  a bare patch — diffing the version's full data against a partial patch emits `null` for
    //  every version key the patch merely does not mention (RFC 7396 = DELETE).
    toSave[id] = versionNode
      ? diffPatch(versionNode.data, patchNodeData(patchNodeData(versionNode.data, saved[id]), sentMap[id]))
      : patchNodeData(saved[id] || {}, sentMap[id] || {});
  }
  return toSave;
}

//--> the server's error code, wherever the gateway left it (detail.error.code is the live shape)
const writeErrorCode = (e) => {
  const data = e?.response?.data;
  return data?.detail?.error?.code ?? data?.error?.code ?? data?.detail?.code ?? null;
};
const isBodyTooLarge = (e) => e?.response?.status === 422 && writeErrorCode(e) === 'BODY_TOO_LARGE';

/*>>> STORE HOOK (02-design.md §H Sequence — Load/Autosave/412, D57 autosave fix-up) <<<*/
//! State lives wherever this hook is called (the Strategy Builder root, Live Analysis) — this hook
//! owns NO module-level state. `versionNodes` is a hook ARGUMENT (kept fresh via a ref, read only
//! when a write actually fires) so every display change can autosave without the caller threading
//! `versionNodes` through every call site — ONE write path (`flushAutosave`), never a second ad hoc
//! PUT. At most one PUT is in flight per strategy ID; different strategy IDs may write concurrently.
export function useVisualOverridesStore(strategyId, versionNodes) {
  const record = useVisualOverridesRecord(strategyId);
  const {
    overridesSaved, currentEtag, canEdit, loadFailed, isLoaded,
    reload, applyRecord, overridesSavedRef, currentEtagRef,
  } = record;

  const [draftOverrides, setDraftOverrides] = useState({});
  const [writeInFlight, setWriteInFlight] = useState(false);
  //! D57 — `writeFailed` surfaces the toolbar's "Not saved — retry"; `isSaving` surfaces
  //! "Saving…" for BOTH the debounce window and the actual in-flight PUT (one combined signal —
  //! the user doesn't care which phase, only that their edit hasn't landed yet).
  const [writeFailed, setWriteFailed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  //! AC7.5 — set (never cleared by a retry) on 422 BODY_TOO_LARGE; one boolean = one notice.
  const [bodyTooLarge, setBodyTooLarge] = useState(false);

  const draftOverridesRef = useRef(draftOverrides);
  const versionNodesRef = useRef(versionNodes);
  draftOverridesRef.current = draftOverrides;
  versionNodesRef.current = versionNodes;

  //! A-11/F-cx-4 — a strategy id can be revisited in the same hook instance, so id equality alone
  //  cannot identify the current lifecycle. Advance this generation during render so an old A
  //  completion is stale immediately on A -> B -> A, before the strategy-change effect runs.
  const strategyIdRef = useRef(strategyId);
  strategyIdRef.current = strategyId;
  const strategyLifecycleRef = useRef({ strategyId, generation: 1 });
  if (strategyLifecycleRef.current.strategyId !== strategyId) {
    strategyLifecycleRef.current = {
      strategyId,
      generation: strategyLifecycleRef.current.generation + 1,
    };
  }

  //! 50-26 — one fresh lane per strategy lifecycle. Different IDs write concurrently; re-entering
  //  an ID gets a new lane whose predecessor promise preserves per-ID single-flight ordering.
  const writeLanesRef = useRef(new Map());
  const writeLaneGenerationRef = useRef(0);
  const createWriteLane = useCallback((capturedStrategyId, predecessorLane = null) => {
    const lane = {
      generation: writeLaneGenerationRef.current + 1,
      lifecycleGeneration: strategyLifecycleRef.current.generation,
      inFlight: false,
      inFlightPromise: null,
      predecessorPromise: predecessorLane?.inFlightPromise || predecessorLane?.predecessorPromise || null,
      waitingForPredecessor: false,
      coalesceNeeded: false,
      debounceTimer: null,
      restoring: false,
    };
    writeLaneGenerationRef.current = lane.generation;
    writeLanesRef.current.set(capturedStrategyId, lane);
    if (lane.predecessorPromise) {
      const predecessorPromise = lane.predecessorPromise;
      const releasePredecessor = () => {
        if (lane.predecessorPromise === predecessorPromise) lane.predecessorPromise = null;
      };
      predecessorPromise.then(releasePredecessor, releasePredecessor);
    }
    return lane;
  }, []);
  const getWriteLane = useCallback((capturedStrategyId) => {
    let lane = writeLanesRef.current.get(capturedStrategyId);
    const isCurrentLifecycle = capturedStrategyId === strategyIdRef.current
      && lane?.lifecycleGeneration === strategyLifecycleRef.current.generation;
    if (!lane || (capturedStrategyId === strategyIdRef.current && !isCurrentLifecycle)) {
      lane = createWriteLane(capturedStrategyId, lane);
    }
    return lane;
  }, [createWriteLane]);
  const cleanupWriteLane = useCallback((capturedStrategyId, lane) => {
    if (writeLanesRef.current.get(capturedStrategyId) !== lane) return;
    if (!lane.inFlight && !lane.inFlightPromise && !lane.predecessorPromise && !lane.waitingForPredecessor
      && !lane.coalesceNeeded && !lane.debounceTimer && !lane.restoring) {
      writeLanesRef.current.delete(capturedStrategyId);
    }
  }, []);
  const isCurrentStrategyLifecycle = useCallback((capturedStrategyId, lifecycleGeneration) => (
    strategyIdRef.current === capturedStrategyId
    && strategyLifecycleRef.current.strategyId === capturedStrategyId
    && strategyLifecycleRef.current.generation === lifecycleGeneration
  ), []);
  const isCurrentWriteLane = useCallback((capturedStrategyId, lane) => (
    isCurrentStrategyLifecycle(capturedStrategyId, lane?.lifecycleGeneration)
    && writeLanesRef.current.get(capturedStrategyId) === lane
  ), [isCurrentStrategyLifecycle]);
  const waitForLanePredecessor = useCallback(async (capturedStrategyId, lane) => {
    const predecessorPromise = lane.predecessorPromise;
    if (!predecessorPromise) return isCurrentWriteLane(capturedStrategyId, lane);
    lane.waitingForPredecessor = true;
    setIsSaving(true);
    try {
      await predecessorPromise;
    } catch {
      // The fresh lifecycle still owns its write after an old lifecycle's failed request settles.
    } finally {
      lane.waitingForPredecessor = false;
    }
    if (lane.predecessorPromise === predecessorPromise) lane.predecessorPromise = null;
    return isCurrentWriteLane(capturedStrategyId, lane);
  }, [isCurrentWriteLane]);
  //! A-3 — a display write is possible ONLY on a saved strategy whose record loaded cleanly and is
  //  editable (`isLoaded && !loadFailed`): never before load (null If-Match -> 428/412), never after
  //  a failed load (the edit would be dropped by the visual-excluded hash dedup), never into node data.
  const displayWritable = !!strategyId && isLoaded && !loadFailed && canEdit;
  const writableRef = useRef(displayWritable);
  writableRef.current = displayWritable;
  //! A-9 — while a Restore PUT is in flight no new display edit is accepted (it would be discarded
  //  by the restore's draft reset); the panel disables the controls from `restoring`.
  const [restoring, setRestoring] = useState(false);
  const flushAutosaveRef = useRef(null);
  const flushOnUnloadRef = useRef(null);
  const unloadSentDraftRef = useRef(null);
  const previousStrategyIdRef = useRef(strategyId);

  useEffect(() => {
    const previousStrategyId = previousStrategyIdRef.current;
    let currentLane = strategyId ? writeLanesRef.current.get(strategyId) : null;
    if (previousStrategyId && previousStrategyId !== strategyId) {
      const previousLane = writeLanesRef.current.get(previousStrategyId);
      if (previousLane) {
        if (previousLane.debounceTimer) clearTimeout(previousLane.debounceTimer);
        previousLane.debounceTimer = null;
        previousLane.coalesceNeeded = false;
        cleanupWriteLane(previousStrategyId, previousLane);
      }
      const predecessorLane = strategyId ? writeLanesRef.current.get(strategyId) : null;
      currentLane = strategyId ? createWriteLane(strategyId, predecessorLane) : null;
    } else if (strategyId) {
      currentLane = getWriteLane(strategyId);
    }
    previousStrategyIdRef.current = strategyId;
    setDraftOverrides({});
    draftOverridesRef.current = {};
    setWriteFailed(false);
    setBodyTooLarge(false);
    setWriteInFlight(!!currentLane?.inFlight);
    setRestoring(!!currentLane?.restoring);
    setIsSaving(!!(currentLane?.inFlight || currentLane?.waitingForPredecessor
      || currentLane?.coalesceNeeded || currentLane?.debounceTimer));
  }, [strategyId, cleanupWriteLane, createWriteLane, getWriteLane]);

  //--> a PUT response body -> state (sanitized the same way as a load)
  const applyPutResponse = useCallback((res) => {
    const body = res?.data?.data || {};
    applyRecord({ overrides: sanitizeVisualRecord(body.overrides), etag: body.etag ?? null });
  }, [applyRecord]);

  //--> a failed PUT: too-large is surfaced (notice, draft kept, NEVER retried); anything else is the
  //    existing "Not saved — retry" state.
  const handleWriteError = useCallback((e, capturedStrategyId, lane) => {
    if (!isCurrentWriteLane(capturedStrategyId, lane)) return;
    if (isBodyTooLarge(e)) {
      lane.coalesceNeeded = false;
      setBodyTooLarge(true);
    } else {
      setWriteFailed(true);
    }
  }, [isCurrentWriteLane]);

  //--> THE one 412 branch (AC3.2, F-arb-2) — autosave, Restore defaults and per-node Restore all PUT through
  //*   this: PUT with the current etag; on 412 refetch the record ONCE, re-derive the payload from
  //*   `buildPayload()` against the FRESH saved record (so a pending delete / a restore is re-applied,
  //*   never lost) and PUT ONCE more with the fresh etag. A second failure (any status) throws to the
  //*   caller, which surfaces it. Resolves `null` when the user has left the strategy meanwhile (A-11).
  //*   50-24: `{ keepalive }` (unload write) applies to the FIRST attempt only; the 412 refetch/retry
  //*   needs a live document and is unchanged.
  const putWithStaleRetry = useCallback(async (
    buildPayload,
    capturedStrategyId,
    lane,
    {
      keepalive = false,
      bypassLane = false,
      ifMatch = currentEtagRef.current,
    } = {},
  ) => {
    const requestIsCurrent = () => (
      bypassLane
        ? isCurrentStrategyLifecycle(capturedStrategyId, lane?.lifecycleGeneration)
        : isCurrentWriteLane(capturedStrategyId, lane)
    );
    if (!requestIsCurrent()) return null;
    try {
      const res = keepalive
        ? await saveVisualOverrides(capturedStrategyId, buildPayload(), ifMatch, { keepalive: true })
        : await saveVisualOverrides(capturedStrategyId, buildPayload(), ifMatch);
      return requestIsCurrent() ? res : null;
    } catch (e) {
      if (!requestIsCurrent()) return null;
      if (e?.response?.status !== 412) throw e;
      const fresh = await loadVisualOverridesRecord(capturedStrategyId);
      if (!requestIsCurrent()) return null;
      applyRecord(fresh);
      const res = await saveVisualOverrides(capturedStrategyId, buildPayload(), currentEtagRef.current);
      return requestIsCurrent() ? res : null;
    }
  }, [applyRecord, currentEtagRef, isCurrentStrategyLifecycle, isCurrentWriteLane]);

  //--> D57 AUTOSAVE core write — PUT saved⊕draft (pruned vs version, same payload rule as
  //*   before), one write in flight; a change arriving while a write is pending COALESCES
  //*   through its strategy lane into exactly one follow-up PUT after the in-flight one returns
  //*   (latest state wins — the follow-up reads `draftOverridesRef`/`overridesSavedRef` fresh,
  //*   never the stale snapshot captured at schedule time). On 412 (R3): refetch the record, merge
  //*   ONLY the still-pending patches (a null leaf = delete) over the FRESH record, and re-PUT the
  //*   full merged record ONCE with the fresh etag — never silently discarding an edit already made.
  //*   On any other failure, or a second 412 on the retry: keep the draft, surface `writeFailed`
  //*   for the "Not saved — retry" link (manual retry re-invokes this same function, no debounce).
  const flushAutosave = useCallback(async () => {
    const capturedStrategyId = strategyId;
    if (!capturedStrategyId || strategyIdRef.current !== capturedStrategyId || !writableRef.current) return;
    const lane = getWriteLane(capturedStrategyId);
    if (!isCurrentWriteLane(capturedStrategyId, lane)) return;
    if (lane.inFlight || lane.waitingForPredecessor) {
      lane.coalesceNeeded = true;
      return;
    }
    if (lane.predecessorPromise) {
      const predecessorReady = await waitForLanePredecessor(capturedStrategyId, lane);
      if (!predecessorReady || !writableRef.current || lane.restoring) {
        if (lane.restoring) lane.coalesceNeeded = false;
        else cleanupWriteLane(capturedStrategyId, lane);
        return;
      }
      // The snapshot is taken after the barrier, so it already includes edits coalesced while waiting.
      lane.coalesceNeeded = false;
    }
    if (lane.debounceTimer) {
      clearTimeout(lane.debounceTimer);
      lane.debounceTimer = null;
    }
    const sentSnapshot = { ...draftOverridesRef.current };
    if (Object.keys(sentSnapshot).length === 0) {
      if (isCurrentWriteLane(capturedStrategyId, lane)) setIsSaving(false);
      cleanupWriteLane(capturedStrategyId, lane);
      return;
    }

    lane.inFlight = true;
    setWriteInFlight(true);
    setIsSaving(true);
    setWriteFailed(false); // a fresh attempt starts clean — "Saving…", not a stale "Not saved"

    //! synchronous on the ref (the coalesced follow-up reads it in the same tick); an entry edited
    //  while the PUT was in flight is a NEW object and survives.
    const clearSentKeys = () => {
      const next = { ...draftOverridesRef.current };
      for (const id of Object.keys(sentSnapshot)) {
        if (next[id] === sentSnapshot[id]) delete next[id];
      }
      draftOverridesRef.current = next;
      setDraftOverrides(next);
    };

    try {
      const inFlightPromise = putWithStaleRetry(
        () => buildOverridesSavePayload(versionNodesRef.current, overridesSavedRef.current, sentSnapshot),
        capturedStrategyId,
        lane,
      );
      lane.inFlightPromise = inFlightPromise;
      const res = await inFlightPromise;
      if (res === null) return; // A-11: late response for a left strategy
      applyPutResponse(res);
      clearSentKeys();
      setBodyTooLarge(false);
    } catch (e) {
      handleWriteError(e, capturedStrategyId, lane);
    } finally {
      lane.inFlight = false;
      lane.inFlightPromise = null;
      if (isCurrentWriteLane(capturedStrategyId, lane)) setWriteInFlight(false);
      const followUp = lane.coalesceNeeded;
      lane.coalesceNeeded = false;
      if (followUp && isCurrentWriteLane(capturedStrategyId, lane)) {
        //  A-11: only the current strategy's LATEST flush may build a follow-up request.
        flushAutosaveRef.current?.();
      } else if (isCurrentWriteLane(capturedStrategyId, lane)) {
        setIsSaving(false);
      }
      cleanupWriteLane(capturedStrategyId, lane);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategyId, putWithStaleRetry, getWriteLane, cleanupWriteLane, isCurrentWriteLane,
    waitForLanePredecessor]);
  flushAutosaveRef.current = flushAutosave;

  //--> Debounce ≤500ms after every display change (D57). If a write is ALREADY in flight when a
  //*   new change lands, skip the timer entirely — `flushAutosave`'s own in-flight guard already
  //*   sets the lane's `coalesceNeeded` flag and follows up when the current write returns.
  const scheduleAutosave = useCallback(() => {
    const capturedStrategyId = strategyId;
    if (!capturedStrategyId || strategyIdRef.current !== capturedStrategyId) return;
    const lane = getWriteLane(capturedStrategyId);
    setIsSaving(true);
    if (lane.inFlight || lane.waitingForPredecessor) {
      lane.coalesceNeeded = true;
      return;
    }
    if (lane.debounceTimer) clearTimeout(lane.debounceTimer);
    lane.debounceTimer = setTimeout(() => {
      lane.debounceTimer = null;
      if (!isCurrentWriteLane(capturedStrategyId, lane)) {
        cleanupWriteLane(capturedStrategyId, lane);
        return;
      }
      flushAutosave();
    }, 500);
  }, [strategyId, flushAutosave, getWriteLane, cleanupWriteLane, isCurrentWriteLane]);

  //--> D57/50-26 — on true unmount, preserve the existing normal flush for an idle debounced draft.
  //*   If a lane is already busy (or a failed draft has no timer), start the independent 50-24
  //*   keepalive before clearing lane bookkeeping so the current strategy's draft is not discarded.
  //*   Strategy switches are handled separately above and never flush the abandoned strategy.
  useEffect(() => () => {
    const capturedStrategyId = strategyIdRef.current;
    const currentLane = capturedStrategyId ? writeLanesRef.current.get(capturedStrategyId) : null;
    let startedNormalFlush = false;
    if (currentLane?.debounceTimer) {
      clearTimeout(currentLane.debounceTimer);
      currentLane.debounceTimer = null;
      if (!currentLane.inFlight && !currentLane.waitingForPredecessor && !currentLane.predecessorPromise) {
        flushAutosaveRef.current?.();
        startedNormalFlush = true;
      }
    }
    if (!startedNormalFlush && Object.keys(draftOverridesRef.current || {}).length > 0) {
      flushOnUnloadRef.current?.();
    }
    for (const lane of writeLanesRef.current.values()) {
      if (lane.debounceTimer) clearTimeout(lane.debounceTimer);
      lane.debounceTimer = null;
      lane.coalesceNeeded = false;
    }
    writeLanesRef.current.clear();
  }, []);

  //--> 50-24 flushOnUnload — pagehide (beforeunload fallback) sends ONE independent keepalive write
  //*   of saved ⊕ draft with the unload-time etag. A normal in-flight PUT, predecessor, or waiting
  //*   lane never suppresses it and the keepalive does not mutate lane bookkeeping. Stated limitations:
  //*   a committed normal PUT can make it lose with 412; an expired token can make it lose with 401;
  //*   an oversized body makes no attempt; and nothing observes the result after the document exits.
  useEffect(() => {
    if (!strategyId) return undefined;
    const capturedStrategyId = strategyId;
    const capturedLifecycleGeneration = strategyLifecycleRef.current.generation;
    const onUnload = () => {
      if (!isCurrentStrategyLifecycle(capturedStrategyId, capturedLifecycleGeneration)) return;
      const lane = getWriteLane(capturedStrategyId);
      if (!writableRef.current || lane.restoring) return;
      const draft = draftOverridesRef.current;
      if (!draft || Object.keys(draft).length === 0) return;
      if (draft === unloadSentDraftRef.current) return; // beforeunload then pagehide = one write
      if (lane.debounceTimer) {
        clearTimeout(lane.debounceTimer);
        lane.debounceTimer = null;
      }
      if (strategyIdRef.current !== capturedStrategyId) return;
      const build = () => buildOverridesSavePayload(versionNodesRef.current, overridesSavedRef.current, draft);
      const bytes = new Blob([JSON.stringify({ overrides: build() })]).size;
      if (bytes > KEEPALIVE_MAX_BODY_BYTES) return;
      unloadSentDraftRef.current = draft;
      const unloadEtag = currentEtagRef.current;
      putWithStaleRetry(
        build,
        capturedStrategyId,
        lane,
        {
          keepalive: true,
          bypassLane: true,
          ifMatch: unloadEtag,
        },
      )
        .then((res) => {
          if (!res) return;
          applyPutResponse(res);
        })
        .catch((e) => handleWriteError(e, capturedStrategyId, lane));
    };
    flushOnUnloadRef.current = onUnload;
    const onPageShow = (event) => {
      //--> bfcache restore: the page survived; converge through the normal 412-aware path
      if (event?.persisted && Object.keys(draftOverridesRef.current || {}).length > 0) scheduleAutosave();
    };
    window.addEventListener('pagehide', onUnload);
    window.addEventListener('beforeunload', onUnload);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('pagehide', onUnload);
      window.removeEventListener('beforeunload', onUnload);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [strategyId, putWithStaleRetry, applyPutResponse, handleWriteError, scheduleAutosave,
    overridesSavedRef, currentEtagRef, getWriteLane, isCurrentStrategyLifecycle]);

  //--> updateNodeDisplay — THE one adapter from a control's dense `updates` to the draft (D40, §H),
  //*   moved here from IndicatorsPanelTab so the Builder and Live Analysis share it.
  //! AC-M2-3 FIX: controls emit a DENSE spread at the top-level key(s) they touch (e.g.
  //  `visual_settings`), so a sub-key a control genuinely REMOVES is OMITTED from `updates`, never
  //  nulled. RFC 7396 `patchNodeData` only deletes on an EXPLICIT null — merging an omission-based
  //  "dense" update straight onto `priorState` would silently no-op the removal. Normalize each
  //  top-level key `updates` touches to an explicit patch (`diffPatch` against what the control was
  //  shown) BEFORE merging, so a genuine removal anywhere in the subtree becomes a real `null`.
  //  A top-level `null` value (e.g. `custom_name: null`, R9 clear) is a null leaf = delete.
  //  Reads refs (never a render-captured snapshot) so several writes in one tick compose.
  const updateNodeDisplay = useCallback((nodeId, rawUpdates) => {
    //! A-3/A-9 — not writable (before load / load failed / read-only) or a Restore PUT is in flight:
    //  the edit is refused here, at the chokepoint — it never reaches the draft, a PUT, or node data.
    const lane = strategyId ? getWriteLane(strategyId) : null;
    if (!writableRef.current || lane?.restoring) return;
    //! A-10 — the same member filter the load path applies (`pickVisualMembers`): a non-member key
    //  never enters the draft (it would 422 forever). null leaves (delete) are members and survive.
    const updates = pickVisualMembers(rawUpdates);
    if (Object.keys(updates).length === 0) return;
    const versionNode = (versionNodesRef.current || []).find((n) => n.id === nodeId);
    const baseline = patchNodeData(versionNode?.data, overridesSavedRef.current[nodeId]);
    const priorState = patchNodeData(baseline, draftOverridesRef.current[nodeId]); // what the control was shown
    const normalizedUpdates = {};
    for (const [k, v] of Object.entries(updates || {})) {
      const priorV = priorState?.[k];
      normalizedUpdates[k] = (isPlainObj(v) && isPlainObj(priorV)) ? diffPatch(priorV, v) : v;
    }
    const candidate = patchNodeData(priorState, normalizedUpdates);
    const nextDraft = { ...draftOverridesRef.current, [nodeId]: diffPatch(baseline, candidate) };
    draftOverridesRef.current = nextDraft;
    setDraftOverrides(nextDraft);
    //! D57 autosave — every display change arms the ≤500ms debounce. ONE call site for the ONE
    //! write path — no second place a change can silently skip autosave.
    scheduleAutosave();
  }, [scheduleAutosave, overridesSavedRef, strategyId, getWriteLane]);

  //--> Restore defaults (R5.6, renamed from "Clear saved") — PUT {} through the real endpoint,
  //*   never a direct store mutation. Cancels any pending debounce AND discards the draft on
  //*   success — a stale pending edit re-autosaving right after would silently UNDO the restore.
  const restoreDefaults = useCallback(async () => {
    const capturedStrategyId = strategyId;
    if (!capturedStrategyId || strategyIdRef.current !== capturedStrategyId) return;
    const lane = getWriteLane(capturedStrategyId);
    if (lane.inFlight || lane.restoring || !writableRef.current) return;
    if (lane.debounceTimer) {
      clearTimeout(lane.debounceTimer);
      lane.debounceTimer = null;
    }
    lane.restoring = true;
    lane.coalesceNeeded = false;
    setRestoring(true);
    if (!await waitForLanePredecessor(capturedStrategyId, lane) || !writableRef.current) {
      lane.restoring = false;
      if (isCurrentWriteLane(capturedStrategyId, lane)) {
        setRestoring(false);
        setIsSaving(false);
      }
      cleanupWriteLane(capturedStrategyId, lane);
      return;
    }
    lane.inFlight = true;
    setWriteInFlight(true);
    setWriteFailed(false);
    try {
      const inFlightPromise = putWithStaleRetry(() => ({}), capturedStrategyId, lane);
      lane.inFlightPromise = inFlightPromise;
      const res = await inFlightPromise;
      if (res === null) return; // A-11
      applyPutResponse(res);
      draftOverridesRef.current = {};
      setDraftOverrides({});
    } catch (e) {
      //! F-arb-2: a stale etag is retried inside putWithStaleRetry; what reaches here failed twice.
      handleWriteError(e, capturedStrategyId, lane);
    } finally {
      lane.inFlight = false;
      lane.inFlightPromise = null;
      lane.restoring = false;
      if (isCurrentWriteLane(capturedStrategyId, lane)) {
        setRestoring(false);
        setWriteInFlight(false);
        setIsSaving(false);
      }
      cleanupWriteLane(capturedStrategyId, lane);
    }
  }, [strategyId, putWithStaleRetry, applyPutResponse, handleWriteError, getWriteLane,
    cleanupWriteLane, isCurrentWriteLane, waitForLanePredecessor]);

  //--> Per-node "Restore default" (M1-3/M2-4, renamed from "Reset") — a draft-only node resets
  //*   purely locally (no PUT; the debounce, if still armed, will just send less — or nothing,
  //*   `flushAutosave` no-ops on an empty snapshot); a saved node issues "saved minus that node"
  //*   directly, leaving every OTHER node's pending draft untouched, then re-arms autosave for
  //*   whatever draft remains.
  const restoreNode = useCallback(async (nodeId) => {
    const dropDraft = () => {
      const next = { ...draftOverridesRef.current };
      delete next[nodeId];
      draftOverridesRef.current = next;
      setDraftOverrides(next);
    };
    if (!(nodeId in overridesSavedRef.current)) {
      dropDraft();
      return;
    }
    const capturedStrategyId = strategyId;
    if (!capturedStrategyId || strategyIdRef.current !== capturedStrategyId) return;
    const lane = getWriteLane(capturedStrategyId);
    if (lane.inFlight || lane.restoring || !writableRef.current) return;
    if (lane.debounceTimer) {
      clearTimeout(lane.debounceTimer);
      lane.debounceTimer = null;
    }
    lane.restoring = true;
    lane.coalesceNeeded = false;
    setRestoring(true);
    if (!await waitForLanePredecessor(capturedStrategyId, lane) || !writableRef.current) {
      lane.restoring = false;
      if (isCurrentWriteLane(capturedStrategyId, lane)) {
        setRestoring(false);
        setIsSaving(false);
      }
      cleanupWriteLane(capturedStrategyId, lane);
      return;
    }
    lane.inFlight = true;
    setWriteInFlight(true);
    setWriteFailed(false);
    try {
      //  saved-minus-this-node is derived from the record AT SEND TIME, so the 412 retry re-applies
      //  the restore on top of the FRESH saved record (another tab's edits to other nodes survive).
      const inFlightPromise = putWithStaleRetry(() => {
        const rest = { ...overridesSavedRef.current };
        delete rest[nodeId];
        return rest;
      }, capturedStrategyId, lane);
      lane.inFlightPromise = inFlightPromise;
      const res = await inFlightPromise;
      if (res === null) return; // A-11
      applyPutResponse(res);
      dropDraft();
    } catch (e) {
      handleWriteError(e, capturedStrategyId, lane);
    } finally {
      lane.inFlight = false;
      lane.inFlightPromise = null;
      lane.restoring = false;
      if (isCurrentWriteLane(capturedStrategyId, lane)) {
        setRestoring(false);
        setWriteInFlight(false);
        setIsSaving(false);
      }
      if (isCurrentWriteLane(capturedStrategyId, lane)
        && Object.keys(draftOverridesRef.current || {}).length > 0) {
        scheduleAutosave();
      }
      cleanupWriteLane(capturedStrategyId, lane);
    }
  }, [putWithStaleRetry, scheduleAutosave, strategyId, applyPutResponse, handleWriteError,
    overridesSavedRef, getWriteLane, cleanupWriteLane, isCurrentWriteLane, waitForLanePredecessor]);

  //--> per-node record entry as a surface sees it: saved ⊕ draft (null leaves applied = deleted).
  //    Read-only consumers (display-name resolver, badges) take THIS, never raw saved/draft.
  const effectiveOverrides = useMemo(() => {
    const ids = new Set([...Object.keys(overridesSaved || {}), ...Object.keys(draftOverrides || {})]);
    const out = {};
    for (const id of ids) {
      out[id] = patchNodeData(
        patchNodeData({}, pickVisualMembers(overridesSaved?.[id])),
        pickVisualMembers(draftOverrides?.[id]),
      );
    }
    return out;
  }, [overridesSaved, draftOverrides]);

  //! P2 render boundary — the store object is MEMOIZED so a React.memo'd consumer skips its host's
  //!  per-tick re-renders (an unstable object here re-rendered every ChartStylingSection on every
  //!  tick — found at Build integration, liveAnalysisDecision suite).
  return useMemo(() => ({
    overridesSaved,
    draftOverrides,
    effectiveOverrides,
    setDraftOverrides,
    currentEtag,
    canEdit,
    loadFailed,
    isLoaded,
    displayWritable,
    restoring,
    writeInFlight,
    writeFailed,
    isSaving,
    bodyTooLarge,
    reload,
    scheduleAutosave,
    flushAutosave,
    updateNodeDisplay,
    restoreDefaults,
    restoreNode,
  }), [overridesSaved, draftOverrides, effectiveOverrides, currentEtag, canEdit, loadFailed, isLoaded,
    displayWritable, restoring, writeInFlight, writeFailed, isSaving, bodyTooLarge, reload, scheduleAutosave, flushAutosave,
    updateNodeDisplay, restoreDefaults, restoreNode]);
}

/*>>> CONTEXT — node cards read + write through the ONE mounted store (R1-T3, R2-T4) <<<*/
//! Defined here (not in a second hook file). The Strategy Builder root mounts the store once and
//! provides `{ strategyId, overridesSaved, draftOverrides, effectiveOverrides, write, canWrite,
//! bodyTooLarge }`; `write(nodeId, patch)` is the Builder's single display-write chokepoint (a saved
//! strategy -> the store's autosave; an unsaved one -> the local node, the R5 creation-time default).
//! With no provider (a surface that is not editable) `write` is null and nothing is applied.
export const VisualOverridesContext = createContext(null);

export function useNodeVisualOverrides(nodeId, nodeData) {
  const ctx = useContext(VisualOverridesContext);
  const overridesSaved = ctx?.overridesSaved;
  const draftOverrides = ctx?.draftOverrides;
  //* effective data of THIS node (version ⊕ saved ⊕ draft) — what the card must evaluate
  //  `isNodeShownOnChart` against. Same object back when the record holds nothing for the node.
  const effectiveData = useMemo(() => {
    if (!ctx || nodeData === undefined) return nodeData;
    return applyVisualOverrides([{ id: nodeId, data: nodeData }], overridesSaved, draftOverrides)[0].data;
  }, [ctx, nodeId, nodeData, overridesSaved, draftOverrides]);
  const write = ctx?.write;
  const boundWrite = useCallback((patch) => write?.(nodeId, patch), [write, nodeId]);
  return {
    hasProvider: !!ctx,
    entry: ctx?.effectiveOverrides?.[nodeId],
    record: ctx?.effectiveOverrides, // the whole map (node id -> entry): upstream-name lookups
    effectiveData,
    write: write ? boundWrite : null,
    canWrite: !!ctx?.canWrite,
    bodyTooLarge: !!ctx?.bodyTooLarge,
  };
}
